import { CaretDownFilled, CaretUpFilled } from "@ant-design/icons";
import {
  Button,
  Card,
  DataTable,
  Dropdown,
  Group,
  Icon,
  Tag,
  TextInput,
  Title,
} from "components/common";
import { ActionButtons } from "components/custom";
import { EDIT_SCRIPT_PATH, TEMPLATE_DETAIL_PATH } from "constants/app-paths";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import theme from "utils/themeVars";

import { Input, Pagination, Row } from "antd";
import PageLayout from "components/common/pageLayout/pageLayout";
import {
  useGetAllEditedPageQuery,
  useGetScriptTypesListQuery,
} from "features/script/api";
import { capitalizeFirstLetter } from "utils";
import { canUserModify } from "../../../presentation/utils";
import styles from "./index.module.less";

const EditedPages = () => {
  const [sortOrder, setSortOrder] = useState({ key: "", order: "asc" });
  const [searchQuery, setSearchQuery] = useState({ page_name: "" });

  const getSortIcon = (key) => {
    return sortOrder.key === key && sortOrder.order === "asc" ? (
      <CaretUpFilled />
    ) : (
      <CaretDownFilled />
    );
  };

  const handleSort = (key) => {
    let order = "asc";
    if (sortOrder.key === key && sortOrder.order === "asc") {
      order = "dsc";
    }
    setSortOrder({ key, order });

    refetch();
  };

  const editedPagesColumns = [
    {
      title: "No.",
      dataIndex: "key",
      className: "tableStartSpace",
      width: 40,
    },
    {
      title: (
        <span onClick={() => handleSort("page_name")}>
          Page Name {getSortIcon("page_name")}
        </span>
      ),
      dataIndex: "name",
      width: 350,
    },
    {
      title: (
        <span onClick={() => handleSort("page_id")}>
          Page ID {getSortIcon("page_id")}
        </span>
      ),
      dataIndex: "pageId",
      width: 60,
    },
    {
      title: "Template Type",
      dataIndex: "type",
      width: 150,
    },
    {
      title: "Specialty",
      dataIndex: "primarySpeciality",
      width: 150,
    },
    {
      title: "Modified Date",
      dataIndex: "modifiedDate",
      width: 90,
    },

    { title: "Status", dataIndex: "status", width: 60 },
    { title: "Actions", dataIndex: "actions", width: 60 },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  let queryObj = Object.fromEntries([...searchParams]);
  const { isMobile } = useSelector((state) => state?.app);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSizeSave, setPageSize] = useState(10);

  const [filterValue, setFilterValue] = useState(null);
  const [visible, setVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(false);
  const user = useSelector((state) => state.app.user?.info);

  const { data, isFetching, isLoading, error, refetch } =
    useGetAllEditedPageQuery(
      {
        // filters: {
        //   page: queryObj?.page || 1,
        //   per_page: queryObj?.per_page || 10,
        // },
        body: {
          ...(queryObj?.page_name && { page_name: queryObj.page_name }),
          ...(sortOrder.key && {
            sortBy: sortOrder.key,
            sortOrder: sortOrder.order || "asc",
          }),
          per_page: pageSizeSave ? pageSizeSave : 10,
          page: page ? page : 1,
        },
      },
      { refetchOnMountOrArgChange: true }
    );

  const { data: options, isLoading: optionsLoading } =
    useGetScriptTypesListQuery();

  useEffect(() => {
    setPage(queryObj.page ? parseInt(queryObj.page) : 1);
    setPageSize(queryObj.per_page ? queryObj.per_page : 10);
  }, [queryObj.page, queryObj.per_page]);

  const getActionsList = (e) => [
    {
      key: "edit_edited_page",
      icon: "MdOutlineEdit",
      color: canUserModify(user, e?.editor_id)
        ? theme["blue200"]
        : theme["gray500"],
      content: "Edit Template",
      title: "Edit Template",
      disabled: !canUserModify(user, e?.editor_id),
      className: "pointer",
      onClick: () => {
        navigate(
          `${EDIT_SCRIPT_PATH.replace(":name", e?.page_name)}?editorId=${
            e?.editor_template_id
          }&version=${e?.version}`
        );
      },
    },
  ];

  const filterList = useMemo(() => {
    return [
      {
        label: "none",
        type: "input",
        key: "page_name",
        placeholder: "Enter page name",
      },
    ];
  }, []);

  const getItems = useCallback(() => {
    return options?.length
      ? options?.map((e) => ({
          label: e?.name,
          key: e?._id,
          onClick: () => {
            navigate(`/scripts/add?template_id=${e?.base_template_id}`);
          },
        }))
      : [];
  }, [options]);

  const tableData = () => {
    return data?.list?.length
      ? data.list.map((e, index) => ({
          ...e,
          key: index + 1,
          name: (
            <div
              style={{ color: theme["primary-color"] }}
              className="pointer"
              onClick={() =>
                navigate(
                  `${TEMPLATE_DETAIL_PATH.replace(
                    ":name",
                    e?.page_name
                  )}?editorId=${e?.editor_template_id}&version=${e?.version}`
                )
              }
            >
              {e?.page_name}
            </div>
          ),
          status: (
            <Tag color="#60A7DB" style={{ borderRadius: "40px" }}>
              {e?.status}
            </Tag>
          ),
          modifiedDate: (
            <p>
              {e?.updated_at
                ? new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }).format(new Date(e?.updated_at))
                : ""}
            </p>
          ),
          pageId: <div>{e?.page_id}</div>,
          type: capitalizeFirstLetter(e?.type),
          actions: <ActionButtons list={getActionsList(e)} />,
        }))
      : [];
  };

  const onHandleChange = (page, pageSize) => {
    setSearchParams({
      ...queryObj,
      page: parseInt(page),
      per_page: pageSize,
    });
  };

  const renderPresentationWeb = () => {
    return (
      <Group gap="16px" className={"listings-content-adjustments"}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextInput
              placeholder="Search by Page Name"
              onChange={(e) => {
                setSearchQuery((prev) => ({
                  ...prev,
                  page_name: e.target.value,
                }));
              }}
              value={queryObj?.page_name}
            />
          </div>
          <Button
            onClick={() => {
              setSearchParams({
                ...queryObj,
                ...searchQuery,
              });
            }}
            text="Search"
            icon={"FiSearch"}
            type="primary"
            size={"large"}
            className="pi-12"
            style={{
              height: 48,
              boxShadow: "none",
              backgroundColor: "#2C72E9",
            }}
            loading={false}
          />
          {queryObj && Object.keys(queryObj)?.length ? (
            <div className={styles.clearAll}>
              <span
                className={styles.clearAllText}
                onClick={() => {
                  setSearchParams({});
                }}
              >
                Clear All
              </span>
            </div>
          ) : null}
          <Dropdown
            options={getItems()}
            trigger="click"
            arrow={false}
            suffixIcon=""
          >
            <Button
              icon="AiOutlinePlus"
              text="New Script"
              type="light"
              color="#1677FF"
              style={{
                backgroundColor: "#f0f8ff",
                borderColor: "#d6e4ff",
                color: "#1677FF",
                height: 48,
                fontWeight: 700,
              }}
            />
          </Dropdown>
        </div>
        <DataTable
          pagination={{
            totalCount: data?.pagination?.totalCount,
            pageOptions: [10, 25, 50],
          }}
          paginationOnBottom={true}
          onChangePage={(queryObj) => setSearchParams(queryObj)}
          columns={editedPagesColumns}
          data={tableData()}
          loading={isLoading}
          error={error}
          onErrorRetry={refetch}
          skeletonLoading={isFetching}
          stickyOffset={0}
          noUrlPush={false}
        />
      </Group>
    );
  };

  const renderPresentationMob = () => {
    return (
      <>
        <Card style={{ marginBottom: "4%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <Input
              label="Search by Name"
              placeholder="Search by Name"
              onChange={(e) => setFilterValue(e?.target?.value)}
              value={filterValue}
            />
            <Button
              onClick={() => {
                setSearchParams({ name: filterValue });
              }}
              icon={"FiSearch"}
              type="primary"
              size={"large"}
              className="pi-12"
              style={{ height: 48, boxShadow: "none" }}
              loading={isFetching}
            />
          </div>
        </Card>
        {data?.list?.length
          ? data?.list.map((item) => (
              <Card
                onClick={() => {
                  navigate(
                    `${TEMPLATE_DETAIL_PATH.replace(
                      ":name",
                      item?.page_name
                    )}?editorId=${item?.editor_template_id}&version=${
                      item?.version
                    }`
                  );
                  // navigate(PRESENTATION_DETAIL_PATH.replace(":id", item._id));
                }}
                style={{ marginBottom: "20px" }}
                className="pointer"
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Title level={3}>{capitalizeFirstLetter(item?.name)}</Title>
                  <div>
                    <div style={{ display: "flex" }}>
                      <Tag color="#60A7DB" style={{ borderRadius: "40px" }}>
                        {item.is_completed ? "Completed" : "Not Completed"}
                      </Tag>
                      <Dropdown
                        options={[
                          {
                            key: 1,
                            label: (
                              <div style={{ display: "flex", gap: "10px" }}>
                                <Icon
                                  icon="MdOutlineEdit"
                                  size="14px"
                                  color={theme["primary-color"]}
                                />
                                <span>Edit</span>
                              </div>
                            ),
                            onClick: (e) => {
                              e?.domEvent?.stopPropagation();
                              navigate(
                                `${EDIT_SCRIPT_PATH.replace(
                                  ":name",
                                  item?.page_name
                                )}?editorId=${
                                  item?.editor_template_id
                                }&version=${item?.version}`
                              );
                            },
                          },
                        ]}
                        suffixIcon=""
                        trigger={"click"}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Button
                          size="small"
                          style={{ background: "transparent" }}
                          icon={"BsThreeDotsVertical"}
                        />
                      </Dropdown>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "baseline",
                  }}
                  className={"gray-800"}
                >
                  PAGE ID: {item?.page_id}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "baseline",
                  }}
                  className={"gray-800"}
                >
                  <div>
                    <Icon icon="BiCalendar" size="14px" />
                  </div>
                  <div>{`Modified Date: ${new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }).format(new Date(item.updated_at))}`}</div>
                </div>
              </Card>
            ))
          : null}
        <Row justify="end" style={{ padding: "10px 16px 16px" }}>
          <Pagination
            onChange={onHandleChange}
            pageSizeOptions={[10, 25, 50]}
            pageSize={pageSizeSave}
            defaultCurrent={page}
            current={page}
            total={data?.pagination?.totalCount}
            showSizeChanger
          />
        </Row>
      </>
    );
  };

  return (
    <PageLayout>
      {isMobile ? renderPresentationMob() : renderPresentationWeb()}
    </PageLayout>
  );
};

export default EditedPages;
